import { useMemo } from 'react';
import { Badge, ProgressBar, Spinner } from 'react-bootstrap';
import type { QuestionnaireQuestion, QuestionnaireResponse } from '../../../infrastructure/QuestionnaireClient';
import { buildQuestionnaireStatistics } from '../utils/responseStatistics';

interface QuestionnaireStatisticsProps {
  questions: QuestionnaireQuestion[];
  responses: QuestionnaireResponse[];
  isLoading: boolean;
}

const compactDate = (value: string): string => new Date(value).toLocaleString();

export function QuestionnaireStatistics({ questions, responses, isLoading }: QuestionnaireStatisticsProps) {
  const statistics = useMemo(() => buildQuestionnaireStatistics(questions, responses), [questions, responses]);

  return (
    <section className="border rounded p-3 bg-white mt-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="h5 mb-0">Answer Statistics</h2>
        {isLoading && <Spinner animation="border" size="sm" />}
      </div>

      <div className="row g-2 mb-3">
        <div className="col-6 col-lg-3">
          <div className="border rounded px-3 py-2 h-100">
            <div className="text-muted small">Total</div>
            <div className="fs-5 fw-semibold">{statistics.totalResponses}</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="border rounded px-3 py-2 h-100">
            <div className="text-muted small">Forwarded</div>
            <div className="fs-5 fw-semibold text-success">{statistics.statusCounts.forwarded}</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="border rounded px-3 py-2 h-100">
            <div className="text-muted small">Pending</div>
            <div className="fs-5 fw-semibold text-warning">{statistics.statusCounts.pending}</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="border rounded px-3 py-2 h-100">
            <div className="text-muted small">Failed</div>
            <div className="fs-5 fw-semibold text-danger">{statistics.statusCounts.failed}</div>
          </div>
        </div>
      </div>

      {statistics.questions.map((question) => (
        <div className="border-top py-3" key={question.questionId}>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
            <div>
              <div className="fw-semibold">{question.label}</div>
              <div className="text-muted small">
                {question.questionId}
                <span className="mx-2">|</span>
                {question.type}
              </div>
            </div>
            <Badge bg="light" text="dark">
              {question.answeredCount}/{statistics.totalResponses} answered
            </Badge>
          </div>

          {question.options.length > 0 ? (
            <div className="d-flex flex-column gap-2">
              {question.options.map((option) => (
                <div key={option.value}>
                  <div className="d-flex justify-content-between small mb-1">
                    <span>{option.label}</span>
                    <span className="text-muted">
                      {option.count} ({option.percentage}%)
                    </span>
                  </div>
                  <ProgressBar now={option.percentage} aria-label={`${option.label} ${option.percentage}%`} />
                </div>
              ))}
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {question.samples.map((sample) => (
                <div className="border rounded px-3 py-2" key={`${question.questionId}-${sample.responseId}`}>
                  <div className="text-muted small">Answer #{sample.responseId} | {compactDate(sample.submittedAt)}</div>
                  <div>{sample.value}</div>
                </div>
              ))}
              {question.samples.length === 0 && (
                <div className="text-muted small border rounded px-3 py-2">No answer samples yet</div>
              )}
            </div>
          )}
        </div>
      ))}

      {!isLoading && questions.length === 0 && (
        <div className="text-muted py-4 text-center border-top">Select a survey with quiz questions</div>
      )}
    </section>
  );
}
